import React, { useState } from "react";

const OfflinePage = () => {
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await fetch("https://www.google.com/favicon.ico", {
        mode: "no-cors",
        cache: "no-store",
      });
      window.location.reload();
    } catch {
      setTimeout(() => {
        setIsRetrying(false);
      }, 700);
    }
  };

  return (
    <>
      <style>{`
        @keyframes float-glow {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.45; }
          50% { transform: translate(-50%, -55%) scale(1.15); opacity: 0.75; }
        }
        @keyframes ripple-wave {
          0% { transform: scale(0.95); opacity: 0.8; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes pulse-dot {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.6; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .neo-retry-btn {
          background: linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #3b82f6 100%);
          box-shadow: 0 8px 24px -4px rgba(79, 70, 229, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.25);
        }
        .neo-retry-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px -4px rgba(79, 70, 229, 0.65), inset 0 1px 0 rgba(255, 255, 255, 0.35);
          filter: brightness(1.08);
        }
        .neo-retry-btn:active:not(:disabled) {
          transform: translateY(1px);
        }
      `}</style>

      <div style={styles.overlay}>
        {/* Background Ambient Glow */}
        <div style={styles.ambientGlow}></div>

        <div style={styles.card}>
          {/* Status Badge */}
          <div style={styles.badge}>
            <span style={styles.badgeDot}></span>
            <span>NO INTERNET CONNECTION</span>
          </div>

          {/* Central Hologram Style Icon */}
          <div style={styles.iconContainer}>
            <div style={styles.ripple}></div>
            <div style={styles.iconBackdrop}>
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#f43f5e"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            </div>
          </div>

          {/* Title & Description */}
          <h2 style={styles.heading}>Connection Lost</h2>
          <p style={styles.subtext}>
            Unable to connect to the server. Your page will automatically restore once your network
            connection is re-established.
          </p>

          {/* Diagnostic Action Tiles */}
          <div style={styles.diagnosticGrid}>
            <div style={styles.diagCard}>
              <div style={styles.diagIcon}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#818cf8"
                  strokeWidth="2.5"
                >
                  <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                  <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                  <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                  <line x1="12" y1="20" x2="12.01" y2="20" />
                </svg>
              </div>
              <div style={styles.diagText}>
                <span style={styles.diagTitle}>Wi-Fi / Cellular</span>
                <span style={styles.diagDesc}>Check status</span>
              </div>
            </div>

            <div style={styles.diagCard}>
              <div style={styles.diagIcon}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#818cf8"
                  strokeWidth="2.5"
                >
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                </svg>
              </div>
              <div style={styles.diagText}>
                <span style={styles.diagTitle}>Auto Sync</span>
                <span style={styles.diagDesc}>Standby mode</span>
              </div>
            </div>
          </div>

          {/* Reconnect Button */}
          <button
            className="neo-retry-btn"
            style={{
              ...styles.button,
              opacity: isRetrying ? 0.75 : 1,
              cursor: isRetrying ? "not-allowed" : "pointer",
            }}
            onClick={handleRetry}
            disabled={isRetrying}
          >
            {isRetrying ? (
              <span style={styles.buttonContent}>
                <span style={styles.spinner}></span>
                Reconnecting...
              </span>
            ) : (
              <span style={styles.buttonContent}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginRight: 9 }}
                >
                  <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                </svg>
                Retry Connection
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(8, 11, 20, 0.82)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999999,
    padding: "20px",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif',
    overflow: "hidden",
  },
  ambientGlow: {
    position: "absolute",
    top: "50%",
    left: "50%",
    width: "420px",
    height: "420px",
    background:
      "radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, rgba(244, 63, 94, 0.15) 50%, transparent 70%)",
    filter: "blur(60px)",
    pointerEvents: "none",
    animation: "float-glow 7s ease-in-out infinite",
  },
  card: {
    position: "relative",
    backgroundColor: "rgba(15, 23, 42, 0.88)",
    borderRadius: "28px",
    maxWidth: "430px",
    width: "100%",
    padding: "40px 32px 36px",
    textAlign: "center",
    boxShadow: "0 30px 80px -15px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "rgba(244, 63, 94, 0.12)",
    color: "#fb7185",
    fontSize: "11px",
    fontWeight: "700",
    padding: "6px 14px",
    borderRadius: "100px",
    marginBottom: "28px",
    border: "1px solid rgba(244, 63, 94, 0.25)",
    letterSpacing: "0.9px",
  },
  badgeDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#f43f5e",
    boxShadow: "0 0 10px #f43f5e",
    animation: "pulse-dot 1.8s ease-in-out infinite",
  },
  iconContainer: {
    position: "relative",
    width: "90px",
    height: "90px",
    margin: "0 auto 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ripple: {
    position: "absolute",
    inset: "0",
    borderRadius: "50%",
    border: "1.5px solid rgba(244, 63, 94, 0.4)",
    animation: "ripple-wave 2.2s cubic-bezier(0, 0.2, 0.8, 1) infinite",
  },
  iconBackdrop: {
    position: "relative",
    width: "76px",
    height: "76px",
    borderRadius: "24px",
    backgroundColor: "rgba(244, 63, 94, 0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(244, 63, 94, 0.2)",
    boxShadow: "0 10px 25px rgba(244, 63, 94, 0.15)",
  },
  heading: {
    fontSize: "24px",
    fontWeight: "800",
    color: "#f8fafc",
    margin: "0 0 10px 0",
    letterSpacing: "-0.4px",
  },
  subtext: {
    fontSize: "14px",
    color: "#94a3b8",
    lineHeight: "1.6",
    margin: "0 0 26px 0",
  },
  diagnosticGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    marginBottom: "28px",
  },
  diagCard: {
    backgroundColor: "rgba(30, 41, 59, 0.6)",
    borderRadius: "14px",
    padding: "12px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    border: "1px solid rgba(255, 255, 255, 0.05)",
    textAlign: "left",
  },
  diagIcon: {
    width: "30px",
    height: "30px",
    borderRadius: "10px",
    backgroundColor: "rgba(99, 102, 241, 0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  diagText: {
    display: "flex",
    flexDirection: "column",
  },
  diagTitle: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#e2e8f0",
  },
  diagDesc: {
    fontSize: "10px",
    color: "#64748b",
  },
  button: {
    width: "100%",
    color: "#ffffff",
    border: "none",
    borderRadius: "14px",
    padding: "15px 24px",
    fontSize: "14px",
    fontWeight: "700",
    letterSpacing: "0.3px",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    outline: "none",
  },
  buttonContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: "17px",
    height: "17px",
    border: "2.5px solid rgba(255, 255, 255, 0.25)",
    borderTop: "2.5px solid #ffffff",
    borderRadius: "50%",
    display: "inline-block",
    marginRight: "9px",
    animation: "spin 0.8s linear infinite",
  },
};

export default OfflinePage;
